import { RefObject, useMemo, useState } from "react"
import classNames from "classnames"
import { UseMutationResult } from "@tanstack/react-query"

import userAvatar from "assets/user.jpg"
import { ReactComponent as ExclamationIcon } from "assets/icons/exclamation.svg"
import {
  CreateMessageBody,
  useMarkUnread,
  useReSendMessage,
  IMessage,
} from "./queries"

import { useMemberId } from ".."
import styles from "./styles.module.scss"
import { usePopup } from "utils/use-popup"
import MessageActions from "./message-actions"
import { useTranslation } from "react-i18next"
import { Avatar } from "antd"
import moment from "moment"
import i18next from "i18next"

interface IProps {
  message: IMessage
  sendMessage: UseMutationResult<unknown, unknown, CreateMessageBody, unknown>
  setReplyingToMsg: (msg: IMessage) => void
  messagesAreaRef: RefObject<HTMLUListElement>
  refsById: any
}

const MessageDetails: React.FC<IProps> = ({
  message,
  sendMessage,
  setReplyingToMsg,
  messagesAreaRef,
  refsById,
}) => {
  const { t } = useTranslation(["date", "suite/home"])
  const [isHovering, setIsHovering] = useState<boolean>(false)

  const actionsModal = usePopup()
  const markUnread = useMarkUnread()
  const discussionId = useMemberId()
  const reSendMessage = useReSendMessage({
    retry: 3,
  })

  const onMarkUnread = (message_id: string) => {
    markUnread.mutate({
      message_id,
      discussion_id: discussionId!,
    })
  }

  const onReSendMessage = (
    message: string,
    message_id: string,
    discussion_id: string
  ) => {
    reSendMessage.mutate({
      message_id,
      discussion_id,
      body: message,
    })
  }

  const goTo = (id: string) => {
    messagesAreaRef.current?.scrollTo({
      top: refsById[id].current?.offsetTop! - 20,
      behavior: "smooth",
    })
    refsById[id].current.style.backgroundColor = "var(--color-neutral-0)"
    setTimeout(function () {
      refsById[id].current.style.backgroundColor = "transparent"
    }, 1000)
  }

  const isSendedMessage = sendMessage.variables?.message_id === message.id
  const isReSendedMessage = reSendMessage.variables?.message_id === message.id
  const isNewMessage = message.id.includes("new-message-")
  const isTheSendingMessage = isSendedMessage && sendMessage.isLoading
  const isTheReSendingMessage = isReSendedMessage && reSendMessage.isLoading

  const messageTime = useMemo(() => {
    moment.locale(i18next.language)
    return !!message.created_at && moment(message.created_at).fromNow()
  }, [i18next.language, message.created_at])

  return (
    <>
      {message.is_unread && (
        <li className={styles.unreadMessage}>
          <span className={classNames("small-heavy", styles.unreadPill)}>
            <svg viewBox="0 0 8 13" className={styles.unreadPillIcon}>
              <path d="M8.16639 0.5H9C10.933 0.5 12.5 2.067 12.5 4V9C12.5 10.933 10.933 12.5 9 12.5H8.16639C7.23921 12.5 6.34992 12.1321 5.69373 11.4771L0.707739 6.5L5.69373 1.52292C6.34992 0.86789 7.23921 0.5 8.16639 0.5Z"></path>
            </svg>
            {t("new", { entity: "", count: 1, context: "male" })}
          </span>
        </li>
      )}
      <li
        data-id={message.id}
        className={classNames(
          styles.message,
          isTheSendingMessage || isTheReSendingMessage
            ? styles.sendingMessage
            : ""
        )}
      >
        {message?.reply_to && (
          <div
            className={styles.replied}
            onClick={() => goTo(message?.reply_to?.id!)}
          >
            <svg width={50} viewBox="0 0 70 25">
              <path d="M70 1.19049C70 0.533001 69.467 0 68.8095 0H6C2.68629 0 0 2.68629 0 6V23.7038C0 24.4197 0.580344 25 1.29623 25V25C2.01212 25 2.59247 24.4197 2.59247 23.7038V8.38098C2.59247 5.06727 5.27876 2.38098 8.59247 2.38098H68.8095C69.467 2.38098 70 1.84798 70 1.19049V1.19049Z" />
            </svg>
            <div>
              <Avatar
                className={styles.avatar}
                src={message.reply_to.creator.avatar_thumb || userAvatar}
              />
              <span className="caption">
                <p>@{message?.reply_to.creator.name} </p>
                <p>{message?.reply_to.body}</p>
              </span>
            </div>
          </div>
        )}
        <div>
          <Avatar
            className={styles.avatar}
            src={message.creator.avatar_thumb || userAvatar}
          />
          <div
            className={styles.wrapper}
            onMouseOver={() => !actionsModal.isOpen && setIsHovering(true)}
            onMouseLeave={() => !actionsModal.isOpen && setIsHovering(false)}
          >
            <p className="caption-heavy">
              {message.creator.name}&nbsp;&nbsp;&nbsp;
              <span className={classNames("caption", styles.time)}>
                |&nbsp;&nbsp; {messageTime}
              </span>
            </p>
            <div
              className={classNames(
                styles.messageContainer,
                isNewMessage && sendMessage.isError ? styles.clickableRow : ""
              )}
              onClick={() =>
                isNewMessage &&
                sendMessage.isError &&
                onReSendMessage(message.body, message.id, discussionId!)
              }
            >
              <div className={styles.editableField}>
                <div style={{ flexDirection: "column", width: "100%" }}>
                  <div className={styles.wrapper}>
                    <div>
                      <p className={classNames(styles.messageBody, "body")}>
                        {message.body}
                      </p>
                      {message.updated_at !== message.created_at && (
                        <p className={classNames("caption", styles.editedTag)}>
                          {t("discussion.edited", {
                            ns: "suite/home",
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isNewMessage && !isTheSendingMessage && sendMessage.isError ? (
                <div className={classNames("caption", styles.errorMessage)}>
                  <ExclamationIcon />
                  <span>
                    {t("discussion.failed_to_send", {
                      ns: "suite/home",
                    })}
                  </span>
                </div>
              ) : (
                !isNewMessage &&
                isHovering && (
                  <MessageActions
                    message={message}
                    actionsModal={actionsModal}
                    onMarkUnread={onMarkUnread}
                    onReply={(msg) => setReplyingToMsg(msg)}
                    setHoveredMessage={() => setIsHovering(false)}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </li>
    </>
  )
}

export default MessageDetails
